'use client';

/**
 * Popup centree « celebration », meme langage visuel que `FeedbackPopup` :
 * carte blanche arrondie, en-tete colore avec un gros emoji + titre, corps court,
 * animations `bounce-in` (carte) + `fade-in` (fond).
 *
 * Contrairement a `FeedbackPopup` (couplee a une recompense XP de QCM), celle-ci
 * est generique : un emoji, un titre, un message. Usage principal = transitions
 * d'auth (bienvenue a la connexion, au revoir a la deconnexion), mais reutilisable
 * pour toute annonce breve et motivante.
 *
 * Ferme d'un tap n'importe ou (fond OU carte) et, si `autoDismissMs` est fourni,
 * se ferme seule apres le delai — le popup de bienvenue ne doit pas bloquer.
 *
 * Mobile-first : carte pleine largeur (max-w-xs), `pb-safe`.
 */

import { useEffect } from 'react';
import { cn } from '../../lib/utils';

export interface CelebrationPopupProps {
  /** Gros emoji de l'en-tete (ex. « 👋 », « 🎉 »). */
  emoji: string;
  /** Titre principal (ex. « Bienvenue ! », « À bientôt ! »). */
  headline: string;
  /** Message secondaire optionnel sous le titre. */
  message?: string;
  onClose: () => void;
  /**
   * Teinte de l'en-tete : `positive` (vert XP, celebratoire) ou `neutral`
   * (bleu marque, plus calme — ex. deconnexion). Defaut `positive`.
   */
  variant?: 'positive' | 'neutral';
  /**
   * Ferme automatiquement apres N ms. Omis ⇒ reste jusqu'au tap. Le timer est
   * nettoye au demontage / au changement de props.
   */
  autoDismissMs?: number;
  /**
   * Libelle d'un bouton d'action. `null`/omis ⇒ pas de bouton (tap + auto-dismiss
   * suffisent), pour une carte epuree facon toast.
   */
  continueLabel?: string | null;
}

export function CelebrationPopup({
  emoji,
  headline,
  message,
  onClose,
  variant = 'positive',
  autoDismissMs,
  continueLabel = null,
}: CelebrationPopupProps) {
  useEffect(() => {
    if (!autoDismissMs) return;
    const id = setTimeout(onClose, autoDismissMs);
    return () => clearTimeout(id);
  }, [autoDismissMs, onClose]);

  return (
    <div
      className="animate-fade-in z-70 fixed inset-0 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={headline}
    >
      {/* Pas de stopPropagation : un tap n'importe ou (fond OU carte) ferme. */}
      <div className="animate-bounce-in pb-safe flex w-full max-w-xs flex-col overflow-hidden rounded-3xl bg-white text-center shadow-2xl sm:max-w-sm">
        {/* En-tete colore */}
        <div
          className={cn(
            'flex shrink-0 flex-col items-center gap-1 px-6 pb-6 pt-8 text-white',
            variant === 'positive'
              ? 'from-xp-500 to-xp-600 bg-linear-to-b'
              : 'from-primary-500 to-primary-600 bg-linear-to-b',
          )}
        >
          <span className="text-5xl leading-none" aria-hidden="true">
            {emoji}
          </span>
          <p className="font-display text-xl font-extrabold">{headline}</p>
        </div>

        {/* Corps : message + bouton optionnel */}
        {(message || continueLabel) && (
          <div className="px-6 py-5">
            {message && <p className="text-ink-600 font-sans text-sm">{message}</p>}
            {continueLabel && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="bg-accent-400 font-display hover:bg-accent-500 mt-4 min-h-11 w-full rounded-2xl px-6 py-2.5 text-sm font-bold text-[#0a2e57] shadow-md transition-all active:scale-[.98]"
              >
                {continueLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
